import React, { useMemo } from 'react';
import {
  DataGrid,
  GridColDef,
  CellTypes,
  Button,
  Icon,
  DataGridProps,
  IconButton,
} from '@fashionnovateam/nova-ui-toolkit';
import Roles, {
  Actions,
  hasPermissionTo,
  RolesType,
} from '../../Lib/Constants/roles';
import { User } from '../../Lib/Api/vendor';

export interface VendorContact {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  title?: string;
  phone?: string;
  last_logged_in_at: string;
  feature_permissions?: {
    allow_printing?: boolean;
  };
}

type TeamMemberRow = {
  id: number;
  firstName: CellTypes.DataGridCellPersonParams;
  lastName: CellTypes.DataGridCellStringParams;
  email: CellTypes.DataGridCellEmailParams;
  role: CellTypes.DataGridCellBadgeParams;
  title: CellTypes.DataGridCellStringParams;
  phone: CellTypes.DataGridCellStringParams;
  last_logged_in_at: CellTypes.DataGridCellDateTimeParams;
  actions: CellTypes.DataGridCellCustomParams;
};

type Props = {
  rows: VendorContact[];
  onEditContact: (contact: VendorContact) => void;
  remove: (id: VendorContact) => void;
  count: number;
  loading: boolean;
  params: {
    filters: string;
    sortBy: string;
    sortOrder: 'ASC' | 'DESC';
    page: number;
  };
  currentUser: User;
  user: User;
} & Omit<DataGridProps<TeamMemberRow>, 'columns'>;

function VendorTeamMembersDataGrid({
  rows,
  onEditContact,
  remove,
  loading,
  params,
  currentUser,
  user,
  ...datagridProps
}: Props) {
  const perms = useMemo(() => {
    const canUpdate = hasPermissionTo(
      'users/:id/team-members',
      currentUser.role as RolesType,
      Actions.UPDATE,
      'MEMBER',
    );
    const canDelete = hasPermissionTo(
      'users/:id/team-members',
      currentUser.role as RolesType,
      Actions.DELETE,
      'MEMBER',
    );
    return {
      canUpdate,
      canDelete,
    };
  }, [currentUser]);

  const columns: GridColDef[] = [
    {
      field: 'contactName',
      headerName: 'Contact Name',
      render: 'person',
      display: 'flex',
      flex: 1,
      sortable: true,
      minWidth: 250,
      disableColumnMenu: true,
    },
    {
      field: 'title',
      headerName: 'Title',
      display: 'flex',
      render: 'string',
      flex: 1,
      sortable: true,
      minWidth: 150,
      disableColumnMenu: true,
    },
    {
      field: 'phone',
      headerName: 'Phone',
      display: 'flex',
      render: 'string',
      flex: 1,
      sortable: false,
      minWidth: 150,
      disableColumnMenu: true,
    },
    {
      field: 'email',
      headerName: 'Email',
      render: 'email',
      flex: 1,
      sortable: true,
      minWidth: 200,
      disableColumnMenu: true,
    },
    {
      field: 'role',
      headerName: 'Role',
      render: 'badge',
      flex: 1,
      sortable: true,
      minWidth: 150,
      disableColumnMenu: true,
    },
    ...(user?.feature_permissions?.allow_printing
      ? [
          {
            field: 'printAccess',
            headerName: 'Print Access',
            render: 'badge' as const,
            display: 'flex' as const,
            flex: 1,
            sortable: true,
            minWidth: 120,
            disableColumnMenu: true,
          },
        ]
      : []),
    {
      field: 'lastLoggedInAt',
      headerName: 'Last Login',
      render: 'dateTime',
      flex: 1,
      sortable: true,
      minWidth: 90,
      disableColumnMenu: true,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      render: 'custom',
      flex: 1,
      sortable: false,
      minWidth: 200,
      disableColumnMenu: true,
    },
  ];

  const getRoleProperties = (role: RolesType) => {
    let color;
    let children;
    const contrast = 'low';

    switch (role) {
      case Roles.VENDOR:
        color = 'primary';
        children = 'Vendor Admin';
        break;
      case Roles.VENDOR_OPERATIONS:
        color = 'yellow';
        children = 'Operations';
        break;
      case Roles.VENDOR_STAFF:
        color = 'purple';
        children = 'Staff';
        break;
    }

    return { color, children, contrast };
  };

  const getPrintAccessProperties = (hasAccess: boolean) => ({
    color: hasAccess ? 'green' : 'neutral',
    children: hasAccess ? 'Yes' : 'No',
    contrast: 'low' as const,
  });

  const transformedRows = useMemo(
    () =>
      rows.map((contact) => {
        const { color, children, contrast } = getRoleProperties(
          contact.role as RolesType,
        );

        return {
          id: contact.id,
          contactName: {
            name: `${contact.first_name} ${contact.last_name}`,
            badgeType: 'vendor',
            className: 'bg-neutral-200',
          },
          title: contact.title,
          email: contact.email,
          role: {
            color,
            children,
            contrast,
          },
          ...(user?.feature_permissions?.allow_printing
            ? {
                printAccess: getPrintAccessProperties(
                  !!contact.feature_permissions?.allow_printing,
                ),
              }
            : {}),
          phone: contact.phone,
          lastLoggedInAt: {
            activity: true,
            time: true,
            date: contact.last_logged_in_at,
          },
          actions:
            params.filters === 'active' ? (
              <div className="flex items-center h-full gap-2">
                {perms.canUpdate && (
                  <Button
                    size="sm"
                    color="secondary"
                    onClick={() => onEditContact(contact)}
                  >
                    Edit
                  </Button>
                )}
                {perms.canDelete && (
                  <IconButton
                    color="secondary"
                    size="sm"
                    onClick={() => remove(contact)}
                    icon={<Icon.DeleteDustbin01 />}
                    label="Remove contact"
                  />
                )}
              </div>
            ) : undefined,
        };
      }),
    [rows, onEditContact],
  );

  return (
    <DataGrid
      stateName="compliance_datagrid"
      hideFilterButton
      hideGroupButton
      columns={columns}
      loading={loading}
      rows={transformedRows}
      initialState={{
        pinnedColumns: {
          left: ['first_name'],
        },
      }}
      sortingMode="client"
      paginationMode="client"
      pageSizeOptions={[50, 100]}
      {...datagridProps}
    />
  );
}

export default VendorTeamMembersDataGrid;
